import { useTranslation } from 'react-i18next';
import { InternationalPhoneField } from '@/components/phone/InternationalPhoneField';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { BrandLeadInputError } from '@/lib/brandLeads/buildBrandLeadInput';
import type { HireClippersDraft } from '@/lib/brandLeads/hireClippersDraft';

type ContactPatch = Partial<
  Pick<HireClippersDraft, 'name' | 'company' | 'email' | 'phoneCountry' | 'phone'>
>;

interface HireClippersContactStepProps {
  draft: HireClippersDraft;
  disabled: boolean;
  error: BrandLeadInputError | null;
  onChange: (patch: ContactPatch) => void;
}

export function HireClippersContactStep({
  draft,
  disabled,
  error,
  onChange,
}: HireClippersContactStepProps) {
  const { t } = useTranslation();

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="space-y-1.5">
        <Label htmlFor="hire-clippers-name">{t('hireClippers.fields.name')}</Label>
        <Input
          id="hire-clippers-name"
          autoComplete="name"
          maxLength={100}
          value={draft.name}
          disabled={disabled}
          aria-invalid={error === 'invalidName'}
          onChange={(event) => onChange({ name: event.target.value })}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="hire-clippers-company">{t('hireClippers.fields.company')}</Label>
        <Input
          id="hire-clippers-company"
          autoComplete="organization"
          maxLength={150}
          value={draft.company}
          disabled={disabled}
          onChange={(event) => onChange({ company: event.target.value })}
        />
      </div>
      <div className="space-y-1.5 sm:col-span-2">
        <Label htmlFor="hire-clippers-email">{t('hireClippers.fields.email')}</Label>
        <Input
          id="hire-clippers-email"
          type="email"
          autoComplete="email"
          maxLength={255}
          value={draft.email}
          disabled={disabled}
          aria-invalid={error === 'invalidEmail'}
          onChange={(event) => onChange({ email: event.target.value })}
        />
      </div>
      <div className="sm:col-span-2">
        <InternationalPhoneField
          id="hire-clippers-phone"
          label={t('hireClippers.fields.whatsapp')}
          countryLabel={t('hireClippers.fields.country')}
          searchLabel={t('hireClippers.fields.search')}
          notFoundLabel={t('hireClippers.fields.notFound')}
          placeholder={t('hireClippers.fields.placeholder')}
          country={draft.phoneCountry}
          phone={draft.phone}
          // Changing country clears the number, as in the course checkout.
          onCountryChange={(phoneCountry) => onChange({ phoneCountry, phone: '' })}
          onPhoneChange={(phone) => onChange({ phone })}
          disabled={disabled}
          invalid={error === 'invalidPhone'}
          required
          privateReplayBlock
        />
      </div>
    </div>
  );
}
